// Page de connexion — lien magique par email uniquement
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IMAGE_CULTURE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_petero-cockpit/artifacts/0v8u87n0_Urugendo3%28%40kingdesignphotography%29_0017.jpg";

export default function Login() {
  const { user, setUser, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("magic_token");

  const [emailMagique, setEmailMagique] = useState("");
  const [resultatMagique, setResultatMagique] = useState(null);
  const [envoiMagique, setEnvoiMagique] = useState(false);
  const [verification, setVerification] = useState(Boolean(magicToken));

  // Vérification automatique d'un lien magique reçu par email
  useEffect(() => {
    if (!magicToken) return;
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link/verify", { token: magicToken });
        setUser(data);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error(apiError(e));
        setVerification(false);
        navigate("/login", { replace: true });
      }
    })();
  }, [magicToken, navigate, setUser]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const demanderLienMagique = async (e) => {
    e.preventDefault();
    setEnvoiMagique(true);
    setResultatMagique(null);
    try {
      const { data } = await api.post("/auth/magic-link/request", { email: emailMagique });
      setResultatMagique(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoiMagique(false);
    }
  };

  if (verification) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background">
        <h1 className="font-serif text-3xl font-bold">Inyange</h1>
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Vérification du lien magique…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* ——— Panneau visuel ——— */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMAGE_CULTURE} alt="Ballet Inyange sur scène" className="absolute inset-0 h-full w-full object-cover object-[50%_62%]" />
        <div className="absolute inset-0 bg-[#2C1A0E]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A0E] via-transparent to-[#2C1A0E]/85" />
        <div className="relative h-full flex flex-col justify-between p-14 text-[#F5EDD6]">
          <div>
            <h1 className="font-serif text-5xl font-bold">Inyange</h1>
            <p className="text-xs uppercase tracking-[0.25em] mt-1 opacity-80">Espace Interne</p>
          </div>
          <div className="space-y-4">
            <p className="font-serif italic text-3xl leading-snug max-w-md">
              « Petero Nzukira — Utabusya abwita ubumera »
            </p>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#D4A843]/25 border border-[#D4A843]/50 px-4 py-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-[#D4A843]" />
              30 &amp; 31 octobre 2026 · Cirque Royal, Bruxelles
            </div>
          </div>
        </div>
      </div>

      {/* ——— Formulaire ——— */}
      <div className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md space-y-8 apparition">
          <div className="lg:hidden">
            <h1 className="font-serif text-4xl font-bold">Inyange</h1>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mt-1">Espace Interne</p>
          </div>
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold">Muraho.</h2>
            <p className="text-muted-foreground mt-2">
              Entrez votre adresse email : vous recevrez un lien de connexion valable 15 minutes.
            </p>
          </div>

          {location.state?.message && (
            <div className="rounded-xl border border-accent/50 bg-accent/15 px-4 py-3 text-sm" data-testid="login-info-message">
              {location.state.message}
            </div>
          )}

          <form onSubmit={demanderLienMagique} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                required
                placeholder="prenom@inyange.com"
                value={emailMagique}
                onChange={(e) => setEmailMagique(e.target.value)}
                className="rounded-full h-12 pl-11"
                data-testid="magic-link-email-input"
              />
            </div>
            <Button type="submit" disabled={envoiMagique} className="w-full rounded-full h-12 hover:-translate-y-0.5 transition-transform" data-testid="magic-link-submit-button">
              {envoiMagique ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recevoir mon lien de connexion"}
            </Button>
          </form>

          {resultatMagique && (
            <div className="rounded-xl border border-success/40 bg-success/10 px-4 py-3 text-sm space-y-2" data-testid="magic-link-result">
              <p>{resultatMagique.message}</p>
              {resultatMagique.demo_link && (
                <p className="break-all">
                  <span className="text-muted-foreground">Mode démo — cliquez ici : </span>
                  <a href={resultatMagique.demo_link} className="text-primary font-medium underline" data-testid="magic-link-demo-link">
                    Ouvrir le lien magique
                  </a>
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground flex items-start gap-2 pt-2 border-t border-border/60">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Espace réservé aux membres d'Inyange ASBL. Aucun mot de passe à retenir : chaque connexion passe par un
            lien à usage unique envoyé à votre adresse email.
          </p>
        </div>
      </div>
    </div>
  );
}
